<template>
  <div class="table-responsive">
    <table class="table table-vcenter table-striped">
      <thead>
      <tr>
        <th class="user-select-none">{{ $t('code') }}</th>
        <th>{{ $t('time') }}</th>
        <th class="text-right" style="min-width: 300px">{{ $t('home') }}</th>
        <th style="min-width: 85px" class="text-nowrap"></th>
        <th class="text-left" style="min-width: 300px">{{ $t('away') }}</th>
        <th class="text-center" style="width: 100px;">{{ $tc('action.name', 2) }}</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="game in games" :key="game.uuid" :class="gameStatusColor(game.status)" :data-testid="'game-row-' + game.uuid">
        <td>
          <div class="btn-group user-select-none">
            <button class="btn btn-info btn-sm"
                    v-if="['NOT_PLAYED', 'SCHEDULED', 'FINISHED'].includes(game.status) && game.teamA && game.teamB"
                    v-tooltip="$t('tooltip.rescheduleGame')"
                    data-testid="game-schedule"
                    @click="$emit('scheduleGameModal', game)"><i class="fa fa-clock-o"></i></button>
            <button class="btn btn-warning btn-sm"
                    v-if="['SCHEDULED'].includes(game.status) && game.teamA && game.teamB"
                    v-tooltip="$t('tooltip.removeFromSchedule')"
                    @click="$emit('cancelScheduledGame', game)"><i class="fa fa-times"></i></button>
          </div>
          <span class="ml-2 text-nowrap" data-testid="game-code">{{ formattedGameCode(game.code) }}</span>
        </td>
        <td><strong style="font-size: 12px;">{{ formatDate(game.time) }}</strong></td>
        <td class="font-w600 font-size-sm text-right">
          <span>
            <router-link v-if="game.teamA" v-html="teamName(game.teamA)"
                         :to="{ name: 'schedules.teams.show', params: {uuid: $route.params.uuid, team: game.teamA.uuid }}">
            </router-link>
            <span v-else style="font-size: 14px;"
                  v-html="teamName(game.teamA)">
            </span>
            <span v-if="game.teamA">
              <i v-if="game.winner_team_id === game.teamA.id"
                 class="badge badge-success ml-2 p-2 user-select-none">{{ $t("game.w") }}</i>
            </span>
            <span v-if="game.teamB">
                <i v-if="game.winner_team_id === game.teamB.id"
                   class="badge badge-danger ml-2 p-2 user-select-none">{{ $t("game.l") }}</i>
            </span>
          </span>

        </td>
        <td class="font-w600 font-size-sm text-center"><span>{{
            game.home_team_score_total
          }} : {{ game.away_team_score_total }}</span>
        </td>
        <td class="font-w600 font-size-sm">
          <span>
            <span v-if="game.teamA">
              <i v-if="game.winner_team_id === game.teamA.id"
                 class="badge badge-danger mr-2 p-2 user-select-none">{{ $t("game.l") }}</i>
            </span>
            <span v-if="game.teamB">
              <i v-if="game.winner_team_id === game.teamB.id"
                 class="badge badge-success mr-2 p-2 user-select-none">{{ $t("game.w") }}</i>
            </span>
            <router-link v-if="game.teamB" v-html="teamName(game.teamB)"
                         :to="{ name: 'schedules.teams.show', params: {uuid: $route.params.uuid, team: game.teamB.uuid }}">
            </router-link>
            <span v-else class="badge badge-warning" style="font-size: 14px;"
                  v-html="teamName(game.teamB)">
            </span>
          </span>
        </td>
        <td class="text-center">
          <div class="btn-group user-select-none">
            <template v-if="game.teamA && game.teamB">
              <router-link
                :to="{ name: 'schedules.games.show', params: {uuid: $route.params.uuid, game: game.uuid }}"
                class="btn btn-sm btn-primary"
                v-tooltip="$t('tooltip.editGame')">
                <i class="fa fa-pencil"></i>
              </router-link>
              <router-link
                :to="{ name: 'schedules.games.showGallery', params: {uuid: $route.params.uuid, game: game.uuid }}"
                class="btn btn-sm btn-primary"
                v-tooltip="$t('game.gallery')">
                <i class="fa fa-image"></i>
              </router-link>
              <pdf-game-download :game="game" :round="roundName" :league="league"></pdf-game-download>
              <docx-game-download :game="game" :round="roundName" :league="league"></docx-game-download>
              <xlsx-game-download :game="game" :round="roundName"
                                  :league="league"></xlsx-game-download>
            </template>
            <button v-if="['NOT_PLAYED', 'SCHEDULED'].includes(game.status)" @click="swapGameTimeVenue(game)" :class="{'btn-success': swapGame?.uuid === game.uuid, 'btn-info': swapGame?.uuid !== game.uuid}" class="btn btn-sm" v-tooltip="$t('game.swapTimeAndVenue')"><i class="fa fa-arrows-v"></i></button>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
// import ApiService from '@/services/api.service'

import XlsxGameDownload from '@/components/shared/XlsxGameDownload.vue'
import PdfGameDownload from '@/components/shared/PdfGameDownload.vue'
import DocxGameDownload from '@/components/shared/DocxGameDownload.vue'
import { gameStatusColor } from '@/lib/status'
import { useGameSwap } from '@/use/useGameSwap'
import { formattedGameCode } from '@/lib/gameCode'

export default {
  name: 'LeGamesTable',
  components: { PdfGameDownload, DocxGameDownload, XlsxGameDownload },
  props: {
    league: {
      type: Object,
      default: () => ({})
    },
    roundName: {
      type: String,
      default: ''
    },
    games: {
      type: Array,
      default: () => ([])
    },
    groups: {
      type: Array,
      default: () => ([])
    }
  },
  setup () {
    const { swapGame, swapGameTimeAndVenue } = useGameSwap()

    return { swapGame, swapGameTimeAndVenue }
  },
  data () {
    return {
      search: ''
    }
  },
  mounted () {
    // this.fetchData()
  },
  watch: {
  },
  methods: {
    formattedGameCode (code) {
      return formattedGameCode(code)
    },
    async swapGameTimeVenue (game) {
      await this.swapGameTimeAndVenue({ ...game, league: { name: this.league.name } })
      this.$emit('swapGameTimeVenue')
    },
    formatDate (time) {
      if (!time) {
        return ''
      }

      return this.$gameTime(time, 'numericDateTime')
    },
    gameStatusColor,
    teamName (team) {
      if (!team) {
        return ''
      }

      if (team?.name) {
        return team.name
      }

      return this.$t('emptySlot')
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
li.disabled a {
  pointer-events: none;
}
</style>
